import type {
  CheckPaymentDocumentType,
  DocumentUploadSdkConfig,
  Maybe,
} from "@highnoteplatform/types";
import {
  DocumentType as UploadDocumentType,
  DocumentUploadSessionStatusCode,
} from "@highnoteplatform/internal-sdk/generated/graphql";

import { documentUploadClient } from "./documentUploadClient";
import { mapError } from "./errorHandler";

const fetchDocumentUploadSession = async (config: DocumentUploadSdkConfig) => {
  const client = documentUploadClient(config);
  const { onError, documentUploadSessionId } = config;
  try {
    const { data } = await client.FindDocumentUploadSession({
      id: documentUploadSessionId,
    });

    if (
      (data.node?.__typename ===
        "USAccountHolderApplicationDocumentUploadSession" ||
        data.node?.__typename === "CheckPaymentDocumentUploadSession") &&
      data.node.status !== undefined
    ) {
      if (
        data.node.status !== DocumentUploadSessionStatusCode.INITIATED &&
        data.node.status !== DocumentUploadSessionStatusCode.IN_PROGRESS
      ) {
        // start a session
        try {
          await client.startDocumentUploadSession({
            input: {
              documentUploadSessionId,
            },
          });

          // get the session
          const result = await client.FindDocumentUploadSession({
            id: documentUploadSessionId,
          });

          return result.data;
        } catch (error) {
          mapError(error, onError);
        }
      } else {
        return data;
      }
    }
  } catch (error) {
    mapError(error, onError);
  }

  return undefined;
};

export const endDocumentUploadSession = async (
  config: DocumentUploadSdkConfig
) => {
  const client = documentUploadClient(config);
  const { onError, documentUploadSessionId } = config;
  try {
    const { data } = await client.endDocumentUploadSession({
      input: {
        documentUploadSessionId,
      },
    });
    if (
      data.endDocumentUploadSession !== undefined &&
      (data.endDocumentUploadSession.__typename ===
        "USAccountHolderApplicationDocumentUploadSession" ||
        data.endDocumentUploadSession.__typename ===
          "CheckPaymentDocumentUploadSession")
    ) {
      return true;
    }
  } catch (error) {
    mapError(error, onError);
  }

  return false;
};

const fetchDocuments = async (config: DocumentUploadSdkConfig) => {
  const { onError } = config;
  const documentsRequired = new Set<UploadDocumentType>();
  const documentsUploaded = new Set<UploadDocumentType>();
  const documentsRecommended = new Set<UploadDocumentType>();

  try {
    const data = await fetchDocumentUploadSession(config);

    if (data?.node?.__typename === "CheckPaymentDocumentUploadSession") {
      const { allowedDocumentTypes = [], documents, status } = data.node;

      if (documents) {
        documents.forEach((item) => {
          if (
            item?.__typename === "DocumentUploadLink" &&
            item.documentType !== undefined &&
            item.status === "COMPLETED"
          ) {
            documentsUploaded.add(item.documentType);
          }
        });
      }

      allowedDocumentTypes
        .map((item: CheckPaymentDocumentType) => UploadDocumentType[item])
        .forEach((item) => {
          if (!documentsUploaded.has(item)) {
            documentsRequired.add(item);
          }
        });

      return {
        documentsRequired,
        documentsUploaded,
        documentsRecommended,
        status,
      };
    }
    if (
      data !== undefined &&
      data.node?.__typename ===
        "USAccountHolderApplicationDocumentUploadSession" &&
      data.node.status !== undefined
    ) {
      const {
        status,
        documents,
        primaryDocumentTypes,
        secondaryDocumentTypes,
        supportingDocumentTypes,
        recommendedDocumentTypes,
      } = data.node;
      if (documents) {
        documents.forEach((item) => {
          if (
            item?.__typename === "DocumentUploadLink" &&
            item.documentType !== undefined &&
            item.status === "COMPLETED"
          ) {
            documentsUploaded.add(item.documentType);
          }
        });
      }

      if (primaryDocumentTypes) {
        primaryDocumentTypes.forEach((item: UploadDocumentType) => {
          documentsRequired.add(item);
        });
      }

      if (secondaryDocumentTypes) {
        secondaryDocumentTypes.forEach((item: UploadDocumentType) => {
          documentsRequired.add(item);
        });
      }

      if (supportingDocumentTypes) {
        supportingDocumentTypes.forEach((item: UploadDocumentType) => {
          documentsRequired.add(item);
        });
      }

      if (Array.isArray(recommendedDocumentTypes)) {
        recommendedDocumentTypes.forEach((item: UploadDocumentType) => {
          documentsRecommended.add(item);
        });
      }

      return {
        documentsRequired,
        documentsUploaded,
        documentsRecommended,
        status,
      };
    }
  } catch (error) {
    mapError(error, onError);
  }

  return undefined;
};

const reloadDom = async (
  config: DocumentUploadSdkConfig
): Promise<Maybe<HTMLDivElement>> => {
  try {
    const reloadElements = document.createElement("div");
    reloadElements.setAttribute("id", "reloaded-document-upload-component");
    const result = await fetchDocuments(config);
    if (result) {
      // load documents uploaded

      if (result.documentsUploaded.size > 0) {
        const documentsUploaded = document.createElement("div");
        documentsUploaded.setAttribute("id", "documents-uploaded");
        const documentsUploadedHeader = document.createElement("h4");
        const documentUploadHearText =
          document.createTextNode("Documents Uploaded");
        documentsUploadedHeader.append(documentUploadHearText);

        documentsUploaded.append(documentsUploadedHeader);
        result.documentsUploaded.forEach((item: string) => {
          const documentUploaded = document.createElement("p");
          const documentUploadedText = document.createTextNode(item);
          const icon = document.createElement("i");
          icon.setAttribute("class", "bi bi-check-circle-fill");
          documentUploaded.append(documentUploadedText);
          documentsUploaded.append(documentUploaded);
          documentUploaded.append(icon);
        });
        reloadElements.append(documentsUploaded);
      }

      const fileUpload = document.createElement("input");

      const documentsRequired = document.createElement("div");
      documentsRequired.setAttribute("id", "documents-required");

      const documentsRequiredHeader = document.createElement("h4");
      const documentsRequiredHeaderText =
        document.createTextNode("Documents Required");
      documentsRequiredHeader.append(documentsRequiredHeaderText);

      const documentType = document.createElement("select");
      documentType.setAttribute("id", "document-sdk-type");
      documentType.setAttribute("class", "round");

      if (result.documentsRequired.size > 0) {
        // add options to dropdown for upload
        result.documentsRequired.forEach((item: UploadDocumentType) => {
          // Match the recommended items
          const isRecommendedItem = result.documentsRecommended.has(item);
          const documentRequired = document.createElement("option");
          documentRequired.setAttribute("value", item);
          documentRequired.append(
            document.createTextNode(
              isRecommendedItem ? `${item} (Recommended)` : item
            )
          );
          documentType.append(documentRequired);
        });
      } else {
        const documentRequired = document.createElement("option");
        fileUpload.setAttribute("disabled", "disabled");

        if (result.status === "SUBMITTED") {
          documentRequired.setAttribute("value", "Session is closed");
          documentRequired.append(document.createTextNode("Session is closed"));
        } else {
          // close session and disable dropdown
          const closedSession = await endDocumentUploadSession(config);

          if (!closedSession) {
            throw new Error("Unable to close document upload session");
          }
          documentRequired.setAttribute("value", "No more documents required");
          documentRequired.append(
            document.createTextNode("No more documents required")
          );
        }

        documentType.append(documentRequired);
        documentType.setAttribute("disabled", "disabled");

        config.onSuccess(true);
      }

      documentsRequired.append(documentsRequiredHeader);
      documentsRequired.append(documentType);

      fileUpload.setAttribute("id", "file-upload-component");
      fileUpload.setAttribute("type", "file");
      fileUpload.setAttribute("accept", ".jpg, .png, .pdf");

      reloadElements.append(documentsRequired);
      reloadElements.append(fileUpload);

      // add divs for spinner
      const overlay = document.createElement("div");
      overlay.setAttribute("class", "document-onUpload-overlay");
      reloadElements.append(overlay);

      const spanner = document.createElement("div");
      spanner.setAttribute("class", "document-onUpload-spanner");
      const loader = document.createElement("div");
      loader.setAttribute("class", "document-onUpload-loader");
      const message = document.createElement("p");
      const messageText = document.createTextNode(
        "Uploading file, please be patient"
      );
      message.append(messageText);
      spanner.append(loader);
      spanner.append(message);
      reloadElements.append(spanner);

      return reloadElements;
    }
  } catch (error) {
    mapError(error, config.onError);
  }

  return undefined;
};

export default reloadDom;
